import { Attachment, DeleteAttachmentPayload } from '@api/models'
import {
  AttachmentApi,
  useDeleteAttachmentMutation,
  useGetMaintenanceAttachmentsQuery,
} from '@api'
import {
  ReactSelectOption,
  ReactSelectOptions,
} from '@components/common/Select/Select.types'
import { useEffect, useState } from 'react'
import { toast } from '@components/common/Toasts'

interface UseMaintenanceAttachmentHookProps {
  maintenanceID: string
}

const useMaintenanceAttachmentHook = ({ maintenanceID }: UseMaintenanceAttachmentHookProps) => {
  const [options, setOptions] = useState<Attachment[]>([])
  const [selectedOption, setSelectedOption] = useState<ReactSelectOption>(null)
  const [attachment, setAttachment] = useState<{
    isLoading: boolean
    url: string
    details: Attachment
  }>(null)
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null)

  const [deleteAttachmentMutation] = useDeleteAttachmentMutation()

  const {
    isLoading: isMaintenanceAttachmentsLoading,
    data: maintenanceAttachments,
  } = useGetMaintenanceAttachmentsQuery(
    {
      maintenanceID,
    },
    {
      skip: !maintenanceID, // Skip if no maintenance ID provided
    }
  )

  let selectOptions: ReactSelectOptions = options.map(a => ({
    label: a.Comment,
    value: a.FileName,
  }))

  // initialize options when maintenance attachments are loaded
  useEffect(() => {
    if (isMaintenanceAttachmentsLoading || !maintenanceAttachments) return

    setOptions(maintenanceAttachments)
    if (maintenanceAttachments.length > 0) {
      setSelectedOption({
        label: maintenanceAttachments[0].Comment,
        value: maintenanceAttachments[0].FileName,
      })
    } else {
      setSelectedOption(null)
    }
  }, [maintenanceAttachments, isMaintenanceAttachmentsLoading])

  // download attachment and parse into blob
  useEffect(() => {
    if (selectedOption === null) {
      setAttachment(null)
    } else {
      setAttachment({ url: '', isLoading: true, details: null })
      AttachmentApi.getInstance()
        .download(selectedOption.value)
        .then((res: any) => {
          setAttachment({
            url: URL.createObjectURL(new Blob([res.data])),
            isLoading: false,
            details: maintenanceAttachments.find(a => a.FileName === selectedOption.value),
          })
        })
        .catch(() => {
          setAttachment(null)
        })
    }
  }, [selectedOption, maintenanceAttachments])

  function handleAttachmentSelection(option: ReactSelectOption) {
    setSelectedOption(option)
  }

  const deleteAttachment = async (params: DeleteAttachmentPayload) => {
    setDeletingAttachmentId(params.id)
    try {
      await deleteAttachmentMutation(params).unwrap()
      toast.success('Attachment deleted successfully')
    } catch (error) {
      toast.error('Failed to delete attachment')
      throw error
    } finally {
      setDeletingAttachmentId(null)
    }
  }

  function isAttachmentDeleting(attachmentId: string): boolean {
    return deletingAttachmentId === attachmentId
  }

  return {
    selectOptions,
    options, // Return the raw attachment options array
    isLoading: isMaintenanceAttachmentsLoading,
    selectedOption,
    handleAttachmentSelection,
    attachment,
    maintenanceID,
    deleteAttachment,
    isAttachmentDeleting,
  }
}

export default useMaintenanceAttachmentHook